/**
 * "She's here" — deciding when a unit's arrival banner should sit at the top
 * of the journey wall.
 *
 * The banner is a one-time moment: the first time a unit becomes the
 * student's current unit and she has not met it before (`arrivalSeenAt`
 * null). The server seeds the first class of a freshly unlocked unit, so the
 * current unit is already "in progress" with no class finished yet; a unit
 * with a finished class is one she has clearly met. It never competes with
 * the seal ceremony, the journey celebration or a read-only wall. Pure, so
 * the rules can be tested without the (large) journey page.
 */

export interface ArrivalCandidate {
  unitId: string;
  status: string;
  locked?: boolean | null;
  classesCompleted?: number | null;
  /** classes of the unit she has opened at least once (finished or not) */
  classesOpened?: number | null;
  arrivalSeenAt?: string | Date | null;
}

/**
 * Returns the unit whose arrival banner should show now, or null.
 *
 * Null when: the wall is not settled yet (a stale copy would announce the
 * wrong unit), something else owns the screen (`blocked`), there is no current
 * unit (everything done or locked), a class of it was already opened or
 * finished (she has met the unit — the simple "up next" card leads instead),
 * the arrival was already met, or it was already shown in this session
 * (`dismissed`).
 */
export function resolveArrivalUnit<T extends ArrivalCandidate>(
  units: readonly T[] | undefined | null,
  opts: {
    wallSettled: boolean;
    blocked: boolean;
    dismissed: ReadonlySet<string>;
  },
): T | null {
  if (!opts.wallSettled || opts.blocked) return null;
  const unit = findWallFocusUnit(units);
  if (!unit) return null;
  if ((unit.classesCompleted ?? 0) > 0) return null;
  if ((unit.classesOpened ?? 0) > 0) return null;
  if (unit.arrivalSeenAt) return null;
  if (opts.dismissed.has(unit.unitId)) return null;
  return unit;
}

/**
 * The unit the wall should open on (expanded, scrolled into view) when the
 * page loads: the unit in progress, else the first unlocked unit not started
 * yet. Null when everything is completed or locked.
 */
export function findWallFocusUnit<T extends ArrivalCandidate>(
  units: readonly T[] | undefined | null,
): T | null {
  if (!units) return null;
  return (
    units.find((u) => u.status === 'in_progress' && !u.locked) ??
    units.find((u) => u.status === 'not_started' && !u.locked) ??
    null
  );
}
